/**
 * kevia-runtime — Session Cognition Layer.
 *
 * The runtime is a lightweight, zero-dependency event pipeline that gives
 * Kevia a working memory of what the user is doing inside Kéves.
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  Component / hook emits pushRuntimeEvent(...)                   │
 * │       ↓                                                         │
 * │  sessionTimeline  — circular buffer (max 120 events)            │
 * │       ↓                                                         │
 * │  runtimeStore     — current state snapshot (module, entity...)  │
 * │       ↓                                                         │
 * │  runtimeSelectors — semantic queries (intent, entity trail)     │
 * │       ↓                                                         │
 * │  orchestrateKevia — [runtime: ...] prefix injected into prompt  │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Prepared for future:
 *  - semantic memory: events can be serialized to Supabase session_events
 *  - embeddings: buildRuntimeContext() output becomes the retrieval query
 *  - proactive intelligence: subscribers can trigger suggestions on state change
 *  - tool orchestration: intent signals can pre-select relevant tools
 *  - interruption recovery: timeline replay restores context after disconnects
 */

export { RT_EVENTS, createRuntimeEvent }            from "./runtimeEvents";
export { pushToTimeline, getTimeline, clearTimeline } from "./sessionTimeline";
export { getState, setState, subscribe, emit }       from "./runtimeStore";
export {
  getCurrentEntity,
  getLastVisitedEntity,
  getRecentModules,
  getDominantIntent,
  getRecentOperationalFocus,
  getRecentEntityNames,
  buildRuntimeContext,
  // FASE 18
  getEmergingIntent,
  getIntentConfidence,
  getOperationalMode,
  getContextualGreeting,
  getSessionHeatmap,
  OPERATIONAL_MODES,
  // FASE 19
  detectSessionDrift,
  scoreRecentContext,
} from "./runtimeSelectors";

export { compressRuntimeTimeline } from "./runtimeCompression";
export { getConversationFocus }    from "./conversationFocus";

/* ─── Primary entry point ─────────────────────────────────────────────── */

import { createRuntimeEvent } from "./runtimeEvents";
import { pushToTimeline }     from "./sessionTimeline";
import { emit }               from "./runtimeStore";

/**
 * The canonical way to record a runtime event.
 *
 * 1. Creates the event from the provided fields
 * 2. Pushes it to the session timeline (with deduplication)
 * 3. Emits it to the store (updates current state, notifies subscribers)
 *
 * Returns the event object, or null if it was deduplicated.
 *
 * @param {{ type, route?, module?, entityType?, entityId?, entityName?, metadata? }} fields
 * @returns {RuntimeEvent | null}
 */
export function pushRuntimeEvent(fields) {
  const event   = createRuntimeEvent(fields);
  const wasAdded = pushToTimeline(event);
  if (!wasAdded) return null;
  emit(event);
  return event;
}

/* ─── Development debug interface (FASE 17.13) ───────────────────────── */

if (process.env.NODE_ENV === "development") {
  /* Expose a debug surface on window for browser console inspection.
   * Never available in production builds (tree-shaken by bundler).
   *
   * Usage in devtools console:
   *   __KEVIA_RUNTIME__.getState()
   *   __KEVIA_RUNTIME__.getTimeline()
   *   __KEVIA_RUNTIME__.getSignals()
   *   __KEVIA_RUNTIME__.getOperationalMode()
   *   __KEVIA_RUNTIME__.getHeatmap()
   *   __KEVIA_RUNTIME__.clear()
   */
  const { getState }                   = require("./runtimeStore");
  const { getTimeline, clearTimeline } = require("./sessionTimeline");
  const { getActiveSignals }           = require("../kevia-signals");
  const {
    getOperationalMode: _getMode,
    getSessionHeatmap,
    detectSessionDrift: _drift,
    scoreRecentContext: _score,
  } = require("./runtimeSelectors");
  const { compressRuntimeTimeline }  = require("./runtimeCompression");
  const { restoreRuntimeSnapshot }   = require("../kevia-persistence");
  const { resolveToolIntent }        = require("../kevia-orchestration");
  const { resolveDataIntent }        = require("../kevia-data/resolveDataIntent");
  const { getCacheSnapshot }         = require("../kevia-data/runtimeCache");
  const { resolvePermissionScope }   = require("../kevia-data/permissionFilters");
  const { getMutationQueue }         = require("../kevia-mutations/executeMutation");
  const { getAuditTrail, getLastMutation } = require("../kevia-mutations/auditLogger");
  const { resolveMutationIntent }    = require("../kevia-mutations/resolveMutationIntent");
  const { getWorkflows, getActiveWorkflows } = require("../kevia-workflows/workflowState");
  const { getWorkflowTimeline }      = require("../kevia-workflows/workflowTimeline");
  const { getPriorityWorkflows }     = require("../kevia-workflows/workflowPriorities");
  const { computeFollowupItems }     = require("../kevia-workflows/followupEngine");
  const { resolveNextActions: _nextActions } = require("../kevia-workflows/resolveNextActions");
  const { resolveWorkflowIntent: _wfIntent } = require("../kevia-workflows/resolveWorkflowIntent");
  const { analyzeWorkflowHealth }            = require("../kevia-workflows/workflowHealth");
  const { getRenderPressureSnapshot }        = require("../kevia-workflows/renderPriorityResolver");
  const { buildContextBudget }               = require("../kevia-core/contextBudgetManager");
  const { buildWorkflowMemory }              = require("../kevia-workflows/workflowMemory");
  const { recoverWorkflows }                 = require("./runtimeRecovery");
  // FASE 24
  const { detectSessionRhythm }             = require("./humanRhythm");
  const { buildContinuityCues }             = require("./conversationContinuity");
  const { getSoftRecommendation }           = require("../kevia-workflows/softRecommendations");
  const { buildOperationalDigest }          = require("../kevia-workflows/operationalDigest");
  // FASE 25.1
  const {
    getPerformanceSnapshot,
    getStoragePressure,
    getLatencyStats,
    getRenderStats,
    getRuntimePressure,
  } = require("../kevia-core/performanceDiagnostics");
  const { getMutationDedupStats }           = require("../kevia-mutations/mutationDeduplication");
  // FASE 25.7
  const { getFieldFeedbackSummary, exportFieldFeedback, clearFieldFeedback } = require("../kevia-core/fieldFeedback");

  window.__KEVIA_RUNTIME__ = Object.freeze({
    // FASE 17
    getState,
    getTimeline,
    clear:             clearTimeline,
    // FASE 18
    getSignals:        getActiveSignals,
    getOperationalMode: _getMode,
    getHeatmap:        getSessionHeatmap,
    // FASE 19
    getSnapshot:       restoreRuntimeSnapshot,
    getCompression:    () => compressRuntimeTimeline(getTimeline()),
    detectDrift:       _drift,
    scoreContext:      _score,
    getToolCandidates: () => resolveToolIntent({
      operationalMode: _getMode(),
      signals:         getActiveSignals(),
    }),
    // FASE 20
    getDataIntent:     () => resolveDataIntent({
      signals:       getActiveSignals(),
      currentModule: getState()?.currentModule ?? "inicio",
    }),
    getToolCache:      getCacheSnapshot,
    getPermissionScope: (appScope) => resolvePermissionScope(appScope),
    getLastToolResults: () => window.__KEVIA_LAST_TOOL_RESULTS__ ?? null,
    // FASE 21
    getMutationQueue,
    getLastMutation,
    getAuditTrail,
    simulateMutation:  (text, entityCtx) => resolveMutationIntent(text, entityCtx ?? null),
    getPendingConfirmations: () => window.__KEVIA_PENDING_ACTION__ ?? null,
    // FASE 22
    getWorkflows,
    getActiveWorkflows,
    getWorkflowTimeline,
    getWorkflowPriorities: () => getPriorityWorkflows(10),
    getPendingFollowups:   () => computeFollowupItems(),
    simulateWorkflow:      (text, entityCtx) => _wfIntent(text, entityCtx ?? null),
    getNextActions:        (wfId) => _nextActions({ workflowId: wfId ?? null }),
    // FASE 23.7 — extended observability
    getContextBudget:      () => buildContextBudget({
      runtimeContext:  null,
      dataContext:     null,
      workflowContext: buildWorkflowMemory(),
    }),
    getRenderPressure:     getRenderPressureSnapshot,
    getWorkflowHealth:     () => analyzeWorkflowHealth(),
    getSuppressedHints:    () => getRenderPressureSnapshot().activeCooldowns,
    getPromptComposition:  () => ({
      workflowMemory: buildWorkflowMemory()?.length ?? 0,
      runtimeContext: getState()?.currentModule ? "active" : "empty",
      activeWorkflows: getActiveWorkflows().length,
    }),
    getPersistenceStats:   () => ({
      workflowsStored: getWorkflows().length,
      activeWorkflows: getActiveWorkflows().length,
      storageKey: "kv_workflows_v1",
    }),
    recoverWorkflows:      (wfs) => recoverWorkflows(wfs),
    // FASE 24
    getSessionRhythm:      () => detectSessionRhythm(),
    getContinuityCues:     (entityMem, entityCtx) => buildContinuityCues({ entityMemory: entityMem ?? {}, entityContext: entityCtx ?? null }),
    getSoftRecommendation: (entityCtx) => getSoftRecommendation({ entityContext: entityCtx ?? null }),
    getDigest:             () => buildOperationalDigest(),
    // FASE 25.1 — Performance observability
    getPerformanceSnapshot,
    getStoragePressure,
    getLatencyStats,
    getRenderStats,
    getRuntimePressure,
    getMutationDedupStats,
    // FASE 25.7 — Field feedback
    getFieldFeedbackSummary,
    exportFieldFeedback:  () => { console.info(exportFieldFeedback()); return "Feedback exported to console."; },
    clearFieldFeedback,
    // FASE 25.6 — Failure simulations (lazy-loaded to keep bundle clean)
    simulate: {
      storageCorruption: () => require("../kevia-core/failureSimulator").simulateStorageCorruption(),
      malformedSnapshot: () => require("../kevia-core/failureSimulator").simulateMalformedSnapshot(),
      staleWorkflows:    () => require("../kevia-core/failureSimulator").simulateStaleWorkflows(),
      workflowChurn:     () => require("../kevia-core/failureSimulator").simulateWorkflowChurn(),
      abortedConfirm:    () => require("../kevia-core/failureSimulator").simulateAbortedConfirmation(),
      clear:             () => require("../kevia-core/failureSimulator").clearAllSimulations(),
    },
  });
}
